import React from "react";
import { useLang } from "../contexts/LanguageContext";
import { Instagram, Linkedin, Heart } from "lucide-react";
import ServiceBadge247 from "./ServiceBadge247";
import WhatsAppIcon from "./WhatsAppIcon";

const IG_URL = "https://www.instagram.com/tripex.sa/";
const LI_URL = "https://www.linkedin.com/company/go-saudi/";
const WA_URL = `https://wa.me/966545670477?text=${encodeURIComponent("أبغى أستفسر عن رحلة — من الموقع")}`;

export default function Footer() {
  const { t, isAr } = useLang();
  return (
    <footer className="relative mt-10 overflow-hidden bg-emerald-deep">
      <div className="absolute inset-0 bg-gradient-to-b from-emerald-deep via-emerald-deep to-black"></div>
      {/* Subtle gradient line accent */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-60" />

      <div className="relative z-10 mx-auto max-w-7xl px-5 pt-20 pb-10 text-cream">
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <img
                src="/tripex-logo-pure-white.png"
                alt="Tripex"
                className="h-16 md:h-20 w-auto"
                draggable="false"
                data-testid="footer-logo"
              />
              <span className="inline-block h-8 w-px bg-gold/60" />
              <span
                className="whitespace-nowrap font-cairo font-extrabold text-sm md:text-base"
                style={{ color: '#C499E8' }}
                data-testid="footer-kicker"
              >
                {t("hero_kicker")}
              </span>
            </div>
            <p className="text-cream/75 text-sm md:text-base leading-relaxed max-w-md"
               style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}>
              {isAr ? "تجارب سعودية استثنائية، مصممة لك من أول خطوة إلى آخر ذكرى." : "Exceptional Saudi experiences, designed for you from first step to last memory."}
            </p>
          </div>

          <div className="md:col-span-3">
            <div className={`text-sage mb-4 ${isAr ? 'font-cairo text-sm font-bold' : 'text-[10px] uppercase tracking-[0.35em] font-display-en'}`}>{isAr ? "استكشف" : "Explore"}</div>
            <ul className="space-y-2.5 text-cream/85 text-sm"
                style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}>
              <li><a href="#about" className="link-uline hover:text-cream">{t("nav_about")}</a></li>
              <li><a href="#cities" className="link-uline hover:text-cream">{t("nav_cities")}</a></li>
              <li><a href="#dana" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new Event('tripex:open-dana')); }} className="link-uline hover:text-cream">{t("nav_dana")}</a></li>
              <li><a href="#start" className="link-uline hover:text-cream">{t("nav_start")}</a></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className={`text-sage mb-4 ${isAr ? 'font-cairo text-sm font-bold' : 'text-[10px] uppercase tracking-[0.35em] font-display-en'}`}>{isAr ? "تواصل معنا" : "Connect"}</div>
            <div className="flex items-center gap-3 mb-5">
              <a
                href={IG_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-ig"
                aria-label="Instagram"
                className="social-pill"
              >
                <Instagram size={18} />
              </a>
              <a
                href={LI_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-li"
                aria-label="LinkedIn"
                className="social-pill"
              >
                <Linkedin size={18} />
              </a>
              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-wa"
                aria-label="WhatsApp"
                className="social-pill"
                style={{ background: 'rgba(37,211,102,0.18)', borderColor: 'rgba(37,211,102,0.4)' }}
              >
                <WhatsAppIcon size={18} color="#25D366" />
              </a>
              <span className="inline-block h-8 w-px bg-cream/15 mx-1" aria-hidden="true" />
              <div className="inline-flex items-center gap-2">
                <ServiceBadge247 variant="outline" size={40} title={isAr ? "خدمة على مدار الساعة" : "24/7 service"} />
                <span
                  className={`text-cream/80 text-xs ${isAr ? 'font-cairo' : 'font-display-en uppercase tracking-[0.18em]'}`}
                >
                  {isAr ? "خدمة على مدار الساعة" : "Always on · 24/7"}
                </span>
              </div>
            </div>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-wa-cta"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold hover:opacity-90 transition shadow-lg"
              style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans', background: '#25D366', color: '#FFFFFF' }}
            >
              <WhatsAppIcon size={16} color="#FFFFFF" />
              {isAr ? "تواصل عبر واتساب" : "Chat on WhatsApp"}
            </a>
          </div>
        </div>

        <div className="pt-8 border-t border-cream/15 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 text-sm text-cream/85" data-testid="footer-made-with-love">
            <Heart size={20} className="fill-emerald" style={{ color: '#51BDB0', filter: 'drop-shadow(0 0 8px rgba(77,182,162,0.4))' }} />
            <span
              style={{ fontFamily: isAr ? 'Cairo' : "'Plus Jakarta Sans', sans-serif" }}
              dir={isAr ? 'rtl' : 'ltr'}
            >
              {t("footer_tag")}
            </span>
          </div>
          <div className="text-xs text-cream/55 font-display-en">
            © {new Date().getFullYear()} {isAr ? "تريبيكس" : "Tripex"} · {t("footer_rights")}
          </div>
        </div>
      </div>
    </footer>
  );
}
